import { createAsyncThunk } from "@reduxjs/toolkit";
import WatchlistAPI from "./watchlistAPI";

// Debounce-aware fetch.
//
// The debounce used to be a manual check inside the thunk body, keyed off
// `state.lastFetchTime` -- which is only ever set in the `.fulfilled` case.
// That meant it could only ever catch a repeat call arriving *after* a
// previous fetch had already completed; a burst of near-simultaneous calls
// that all start before any of them resolves (e.g. two independently-
// mounted components both fetching on mount, or React 18 StrictMode's
// double-invoke in dev) all read `lastFetchTime` as stale/null and sailed
// straight through -- confirmed live via a Playwright network trace showing
// up to 6 real `/me/stocks` requests fired within ~1.3s of a single page
// load. `condition` runs synchronously before `.pending` is even
// dispatched, so checking `state.loading` here (set synchronously by the
// very first call's `.pending`) actually closes the race, instead of
// racing it.
export const fetchWatchlistThunk = createAsyncThunk(
  "watchlist/fetch",
  async ({ skip = 0, limit = 50 } = {}, { rejectWithValue }) => {
    try {
      return await WatchlistAPI.getFollowedStocks({ skip, limit });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
  {
    condition: ({ skip = 0, limit = 50 } = {}, { getState }) => {
      const state = getState().watchlist;
      if (state.loading) return false; // a fetch is already in flight

      const now = Date.now();
      const sameParams = JSON.stringify(state.lastFetchParams) === JSON.stringify({ skip, limit });
      if (state.lastFetchTime && sameParams && now - state.lastFetchTime < 1000) {
        return false; // an identical fetch completed less than a second ago
      }
      return true;
    },
  }
);

// Follow Stock (optimistic + background refresh)
export const followStockThunk = createAsyncThunk(
  "watchlist/follow",
  async ({ stockId, symbol }, { rejectWithValue, dispatch }) => {
    try {
      await WatchlistAPI.followStock(stockId, symbol);

      // Background refresh
      dispatch(fetchWatchlistThunk({ skip: 0, limit: 50 }));

      return { stockId, symbol };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Unfollow Stock (optimistic + background refresh)
export const unfollowStockThunk = createAsyncThunk(
  "watchlist/unfollow",
  async ({ stockId, symbol }, { rejectWithValue, dispatch }) => {
    try {
      await WatchlistAPI.unfollowStock(stockId, symbol);

      // Background refresh
      dispatch(fetchWatchlistThunk({ skip: 0, limit: 50 }));

      return { stockId, symbol };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);
